import { useId } from "react";

/** Sunburst mark: 13 tapered rays over an arch sitting on a baseline. */
const RAYS = [
  { angle: 6, reach: 26 },
  { angle: 20, reach: 21 },
  { angle: 34, reach: 27 },
  { angle: 48, reach: 22 },
  { angle: 62, reach: 28 },
  { angle: 76, reach: 23 },
  { angle: 90, reach: 32 },
  { angle: 104, reach: 23 },
  { angle: 118, reach: 28 },
  { angle: 132, reach: 22 },
  { angle: 146, reach: 27 },
  { angle: 160, reach: 21 },
  { angle: 174, reach: 26 },
];

const CX = 34;
const CY = 44;
const INNER = 15;
const HALF_W = 1.45;

const rayPath = ({ angle, reach }: { angle: number; reach: number }) => {
  const a = (angle * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  // Perpendicular to the radial direction, in SVG's y-down space.
  const px = Math.sin(a);
  const py = Math.cos(a);

  const tipX = CX + reach * cos;
  const tipY = CY - reach * sin;
  const baseX = CX + INNER * cos;
  const baseY = CY - INNER * sin;

  const ax = baseX + px * HALF_W;
  const ay = baseY + py * HALF_W;
  const bx = baseX - px * HALF_W;
  const by = baseY - py * HALF_W;

  return `M${tipX.toFixed(2)} ${tipY.toFixed(2)}L${ax.toFixed(2)} ${ay.toFixed(2)}L${bx.toFixed(2)} ${by.toFixed(2)}Z`;
};

type LogoProps = {
  className?: string;
  /** Brighter metal for dark backgrounds. */
  tone?: "default" | "light";
};

export const Logo = ({ className, tone = "default" }: LogoProps) => {
  const id = useId();
  const gold = `${id}-gold`;

  const stops =
    tone === "light"
      ? ["#F5DA9B", "#E3BE66", "#D9AE55"]
      : ["#E4C173", "#CE9C34", "#B4842B"];

  return (
    <svg
      className={className}
      viewBox="0 0 260 60"
      role="img"
      aria-label="Durgastra Innovations"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={gold} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor={stops[0]} />
          <stop offset="52%" stopColor={stops[1]} />
          <stop offset="100%" stopColor={stops[2]} />
        </linearGradient>
      </defs>

      <g fill={`url(#${gold})`}>
        {RAYS.map((ray) => (
          <path key={ray.angle} d={rayPath(ray)} />
        ))}

        {/* Arch */}
        <path d={`M${CX - 13.5} ${CY}A13.5 13.5 0 0 1 ${CX + 13.5} ${CY}L${CX + 8.6} ${CY}A8.6 8.6 0 0 0 ${CX - 8.6} ${CY}Z`} />

        {/* Baseline */}
        <path d={`M${CX - 33} ${CY + 1.1}L${CX + 33} ${CY + 1.1}L${CX + 31} ${CY + 4}L${CX - 31} ${CY + 4}Z`} />

        {/* Divider */}
        <rect x="82.5" y="15" width="1.1" height="30" />

        <text
          x="99"
          y="33.5"
          fontFamily="Aptos, 'Segoe UI', 'Trebuchet MS', sans-serif"
          fontSize="17"
          fontWeight="600"
          letterSpacing="3.6"
        >
          DURGASTRA
        </text>
        <text
          x="100"
          y="48.5"
          fontFamily="Aptos, 'Segoe UI', 'Trebuchet MS', sans-serif"
          fontSize="10.2"
          fontWeight="500"
          letterSpacing="5.15"
        >
          INNOVATIONS
        </text>
      </g>
    </svg>
  );
};
